/**
 * CRM page — "What Dink keeps on every contact" tab, the default (DNKADM-0499; DNKAPI-0716).
 *
 *  - A plain intro, then the Dink-owned fields as a table: the field name, what it means in
 *    one line ({@link fieldMeaning}), and a check when it exists in the CRM.
 *  - A status line: "Last updated <relative time>. <n> contacts up to date, <n> failing."
 *    When any contact is failing, the plain error sample and a "Try again" (runs a sync).
 *  - "Set up fields" (`POST …/fields/ensure`), shown while any field is missing. No write
 *    scope is a 200 whose `errorCode` is `CRM_FIELDS_NO_SCOPE`: it reads the fixed sentence
 *    (never the code) and the page re-reads. A field the CRM refused (`outcome: FAILED`) says
 *    so plainly; fields created outside the "Dink" folder get a note.
 *  - "Fill in history" (`POST …/sync`, which returns the run — a live one if one is already
 *    going): progress from its run id (`GET …/sync/{runId}`) until its status is anything but
 *    RUNNING ({@link isSyncRunDone}), then the counts and a re-read. `DONE` reads the counts
 *    (failed contacts included); `FAILED` (stopped early) says so. The overview carries no
 *    last-run id, so a page opened mid-run shows "Last run: Updating now." and Fill in history
 *    rejoins that run (the POST returns the live run). The last run's status word reads in
 *    plain words ({@link runResultWords}).
 *  - The "Track a service" card ({@link TrackServicesCard}), the example list built on the
 *    first tracked service (hidden when none), and — only when the server says Dink already
 *    mirrors them — a note that Dink also writes the membership and credit fields.
 *
 * White-label: server text (the error sample, server messages) is brand-scrubbed; the page
 * says {@link CRM_DISPLAY_NAME}, never the brand.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { ScreenError } from '@/components/ScreenError';
import { Icon } from '@/theme';
import {
  crmErrorMessage,
  ensureCrmFields,
  getCrmOverview,
  getCrmSyncRun,
  crmCodeMessage,
  isSyncRunDone,
  runResultWords,
  runStopped,
  startCrmSync,
  type CrmOverview,
  type CrmSyncRun,
} from '@/api/crm';
import { formatRelativeTime } from '@/utils/relativeTime';
import { CRM_DISPLAY_NAME, scrubBrand } from '@/utils/whitelabel';
import { VISIT_DEFINITION, fieldMeaning, fieldRows } from './crmFieldCopy';
import { TrackServicesCard } from './TrackServicesCard';

/** How often a running sync is re-read. */
const DEFAULT_POLL_MS = 2000;
/** Stop watching a run after this many reads (~10 minutes at the default pace). */
const MAX_POLLS = 300;

type SyncView =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'running'; run: CrmSyncRun | null }
  | { kind: 'done'; run: CrmSyncRun }
  | { kind: 'stale' }
  | { kind: 'error'; message: string };

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "Last updated 10 min ago. 40 contacts up to date, 2 failing." */
function statusLine(overview: CrmOverview): string {
  const when = formatRelativeTime(overview.sync.lastRunAt);
  const head = when === null ? 'Not updated yet.' : `Last updated ${when}.`;
  const { contactsInSync, contactsFailing } = overview.sync;
  return `${head} ${plural(contactsInSync, 'contact', 'contacts')} up to date, ${contactsFailing} failing.`;
}

export function CrmFieldsTab({ pollMs = DEFAULT_POLL_MS }: { pollMs?: number } = {}) {
  const showToast = useToast();
  const overview = useResource(useCallback(() => getCrmOverview(), []), []);
  const refetch = overview.refetch;

  // ---- Set up fields (ensure) ----
  const [ensuring, setEnsuring] = useState(false);
  const [ensureError, setEnsureError] = useState<string | null>(null);
  const [unfiled, setUnfiled] = useState(false);
  const [failedFields, setFailedFields] = useState(0);

  async function handleEnsure() {
    setEnsuring(true);
    setEnsureError(null);
    setUnfiled(false);
    setFailedFields(0);
    try {
      const result = await ensureCrmFields();
      setUnfiled(result.unfiled);
      if (result.errorCode !== null) {
        // A 200 that could not set the fields up (no write scope): plain words, never the code.
        setEnsureError(
          crmCodeMessage(result.errorCode) ?? 'The fields couldn’t be set up. Please try again.',
        );
      } else if (result.failedCount > 0) {
        setFailedFields(result.failedCount);
      } else {
        showToast('Fields are set up', 'success');
      }
    } catch (err) {
      setEnsureError(crmErrorMessage(err, 'The fields couldn’t be set up. Please try again.'));
    } finally {
      setEnsuring(false);
      refetch();
    }
  }

  // ---- Fill in history (sync) ----
  const [sync, setSync] = useState<SyncView>({ kind: 'idle' });
  const [runId, setRunId] = useState<string | null>(null);
  const pollsRef = useRef(0);

  async function handleSync() {
    setSync({ kind: 'starting' });
    try {
      // The POST returns the run (an already-live one as-is); follow its id either way.
      const run = await startCrmSync();
      if (run.runId === '') throw new Error('no run id');
      pollsRef.current = 0;
      setSync({ kind: 'running', run });
      setRunId(run.runId);
    } catch (err) {
      setSync({ kind: 'error', message: crmErrorMessage(err, 'History couldn’t be filled in. Please try again.') });
    }
  }

  // Watch the run: read it now, then every `pollMs` until it is done.
  useEffect(() => {
    if (runId === null) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async () => {
      pollsRef.current += 1;
      try {
        const run = await getCrmSyncRun(runId);
        if (cancelled) return;
        if (isSyncRunDone(run)) {
          setSync({ kind: 'done', run });
          setRunId(null);
          refetch();
          return;
        }
        setSync({ kind: 'running', run });
      } catch (err) {
        if (cancelled) return;
        setSync({ kind: 'error', message: crmErrorMessage(err, 'We lost track of the update. Check back in a few minutes.') });
        setRunId(null);
        return;
      }
      if (pollsRef.current >= MAX_POLLS) {
        setSync({ kind: 'stale' });
        setRunId(null);
        return;
      }
      timer = setTimeout(() => void read(), pollMs);
    };
    void read();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [runId, pollMs, refetch]);

  const syncBusy = sync.kind === 'starting' || sync.kind === 'running';

  if (overview.status === 'loading' && overview.data === null) {
    return (
      <Skeleton
        variant="rounded"
        height={260}
        role="status"
        aria-label="Loading the fields"
        sx={{ borderRadius: 3 }}
      />
    );
  }
  if (overview.data === null) {
    return <ScreenError error={overview.error} what="the fields" onRetry={refetch} />;
  }

  const data = overview.data;
  const connected = data.connection.connected;
  const rows = fieldRows(data.fields, data.trackedServices);
  const anyMissing = rows.some((r) => !r.exists);
  const firstTracked = data.trackedServices[0];
  const mirrorNote = data.existingMirror.membershipFields || data.existingMirror.creditBucketsMapped > 0;
  const errorSample = scrubBrand(data.sync.lastErrorSample);
  const lastRunWords = runResultWords(data.sync.lastRunResult);

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 880 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Dink keeps these fields up to date on every contact, so you can build lists and campaigns
        in your {CRM_DISPLAY_NAME}. Your {CRM_DISPLAY_NAME} can read them; Dink owns them.
      </Typography>

      {!connected && (
        <Alert severity="info">
          Your {CRM_DISPLAY_NAME} isn’t connected, so Dink can’t keep these fields yet. Ask
          Journix support.
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {anyMissing && (
          <Button
            variant="contained"
            startIcon={<Icon name="add_circle" />}
            onClick={() => void handleEnsure()}
            disabled={!connected || ensuring}
          >
            {ensuring ? 'Setting up…' : 'Set up fields'}
          </Button>
        )}
        <Button
          variant={anyMissing ? 'outlined' : 'contained'}
          startIcon={<Icon name="history" />}
          onClick={() => void handleSync()}
          disabled={!connected || syncBusy}
        >
          {syncBusy ? 'Filling in…' : 'Fill in history'}
        </Button>
      </Stack>

      {ensureError && (
        <Alert severity="error" role="alert">
          {ensureError}
        </Alert>
      )}
      {failedFields > 0 && (
        <Alert severity="warning">
          Your {CRM_DISPLAY_NAME} refused {failedFields === 1 ? 'one field' : `${failedFields} fields`}.
          Try “Set up fields” again.
        </Alert>
      )}
      {unfiled && (
        <Alert severity="info">
          The fields were created outside a “Dink” folder in your {CRM_DISPLAY_NAME}. You can
          move them into a folder there.
        </Alert>
      )}
      <SyncProgress view={sync} />

      <Box>
        <Typography variant="body2">
          {statusLine(data)}
        </Typography>
        {lastRunWords !== null && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Last run: {lastRunWords}.
          </Typography>
        )}
        {data.sync.contactsFailing > 0 && (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 0.5 }}>
            {errorSample && (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                {errorSample}
              </Typography>
            )}
            <Button size="small" onClick={() => void handleSync()} disabled={!connected || syncBusy}>
              Try again
            </Button>
          </Stack>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
        <Table size="small" aria-label="Fields Dink keeps">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>What it means</TableCell>
              <TableCell align="center">In your {CRM_DISPLAY_NAME}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.name}>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{row.name}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {fieldMeaning(row.name, data.trackedServices) ?? '—'}
                </TableCell>
                <TableCell align="center">
                  {row.exists ? (
                    <Icon name="check_circle" title={`In your ${CRM_DISPLAY_NAME}`} sx={{ color: 'success.main', fontSize: 20 }} />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Not yet
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1.5 }}>
        {VISIT_DEFINITION}
      </Typography>
      {mirrorNote && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Dink also writes your membership and credit fields.
        </Typography>
      )}

      <TrackServicesCard tracked={data.trackedServices} onChanged={refetch} />

      {firstTracked && (
        <Paper
          variant="outlined"
          aria-label="Example list"
          sx={{ p: 2, borderRadius: 3, bgcolor: 'action.hover', borderStyle: 'dashed' }}
        >
          <Typography variant="body2">
            Example list: had a {firstTracked.name} and never bought anything ={' '}
            {firstTracked.fieldName} is not empty, and Dink Has Purchased is No.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}

function SyncProgress({ view }: { view: SyncView }) {
  if (view.kind === 'starting' || view.kind === 'running') {
    const run = view.kind === 'running' ? view.run : null;
    const done = run ? run.written + run.unchanged + run.failed : 0;
    const total = run?.total ?? 0;
    return (
      <Box role="status" aria-label="Fill in history progress">
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {total > 0 ? `Filling in history: ${done} of ${total} contacts` : 'Filling in history…'}
        </Typography>
        {total > 0 ? (
          <LinearProgress variant="determinate" value={Math.min(100, (done / total) * 100)} />
        ) : (
          <LinearProgress />
        )}
      </Box>
    );
  }
  if (view.kind === 'done') {
    const { run } = view;
    if (runStopped(run.status)) {
      return (
        <Alert severity="error" role="alert">
          {runResultWords(run.status)}: {run.written} updated, {run.unchanged} already up to date,{' '}
          {run.failed} failed. Try filling in history again.
        </Alert>
      );
    }
    return (
      <Alert severity={run.failed > 0 ? 'warning' : 'success'}>
        History filled in: {run.written} updated, {run.unchanged} already up to date, {run.failed}{' '}
        failed.
      </Alert>
    );
  }
  if (view.kind === 'stale') {
    return <Alert severity="info">Still filling in history. Check back in a few minutes.</Alert>;
  }
  if (view.kind === 'error') {
    return (
      <Alert severity="error" role="alert">
        {view.message}
      </Alert>
    );
  }
  return null;
}
